import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import SmartDisplayOutlinedIcon from "@mui/icons-material/SmartDisplayOutlined";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import MainHeading from "../../components/main-heading.component";
import { _get, _post } from "../../services/api.service";
import { _notify, NotificationType } from "../../services/notification.service";
import { getContentFormatIcon } from "../../utils/contentFormatIcons";
import "../content-engine-v2/content-engine.css";
import "../content-engine-v2/select-format/SelectContentFormat.css";
import { FormatPreview } from "../content-engine-v2/select-format/SelectContentFormat";
import "../video-create/CreateVideo.css";
import "./QuickVideo.css";

interface ContentFormat {
  id: number;
  name: string;
  slug: string;
  description: string;
  short_description: string;
  best_for: string[];
  example_title: string;
  preview_type: string;
  preview_config: Record<string, unknown>;
  icon: string;
  is_active: boolean;
}

const QUICK_EXAMPLES = [
  "5 things to check before buying a flat in Gurugram",
  "7 mistakes first-time home buyers make",
  "Home loan tips nobody tells you",
];

const GENERATING_STEPS = ["Creating project", "Queued for generation"];

const QuickVideo: React.FC = () => {
  const navigate = useNavigate();
  const [formats, setFormats] = useState<ContentFormat[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<ContentFormat | null>(null);
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generatingStep, setGeneratingStep] = useState(0);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 700px)").matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 700px)");
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);
    updateIsMobile();
    mediaQuery.addEventListener("change", updateIsMobile);
    return () => mediaQuery.removeEventListener("change", updateIsMobile);
  }, []);

  useEffect(() => {
    _get("/content-engine/content-formats/", {}, true).then((response: ContentFormat[] | null) => {
      const videoFormats = (response ?? []).filter((format) =>
        ["listicle", "comparison"].includes(format.slug),
      );
      setFormats(videoFormats);
    });
  }, []);

  const handleFormatSelect = (format: ContentFormat) => {
    if (!format.is_active || generating) return;
    setSelectedFormat(format);
  };

  const canGenerate = Boolean(selectedFormat && topic.trim().length >= 2 && !generating);

  const handleCreateVideo = async () => {
    const trimmedTopic = topic.trim();
    if (!selectedFormat || trimmedTopic.length < 2 || generating) return;

    setGenerating(true);
    setGeneratingStep(0);

    try {
      const project = await _post(
        "video-engine/projects/",
        {
          mode: "quick",
          topic: trimmedTopic,
          content_format_id: selectedFormat.id,
          language: "en",
        },
        {},
        false,
        true,
      );

      if (!project?.id) throw new Error("Video project could not be created");
      navigate(`/video-projects/${project.id}`);
    } catch (error) {
      console.error("Quick video creation failed", error);
      _notify(NotificationType.ERROR, "Failed to create video. Please try again.");
    } finally {
      setGenerating(false);
      setGeneratingStep(0);
    }
  };

  return (
    <>
      <div className="container-fluid quick-video-page select-format-page">
        <MainHeading
          title="Quick Video"
          subTitle="Enter your city or topic and get a ready-to-post Reel."
        />

        <div className="qv-layout">
          <section className="cv-creator-card qv-card">
            <div className="cv-card-inner qv-card-inner">
              <div className="qv-section">
                <div className="qv-section-head">
                  <h2 className="qv-section-title">What&apos;s your video about?</h2>
                  <p className="qv-section-subtitle">
                    Enter a short topic. We&apos;ll write the script and start building your video.
                  </p>
                </div>

                <div className="input-wrap">
                  <textarea
                    maxLength={100}
                    placeholder="e.g. 5 productivity hacks for remote workers"
                    value={topic}
                    className="text-area"
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setTopic(e.target.value)
                    }
                  />
                  <div className="char-counter">{topic.length} / 100</div>
                </div>

                <p className="qv-examples-label">Try an example</p>
                <div className="qv-examples">
                  {QUICK_EXAMPLES.map((example) => (
                    <button
                      key={example}
                      type="button"
                      className="qv-example-chip"
                      onClick={() => setTopic(example)}
                      disabled={generating}
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>

              <div className="qv-section qv-section-formats">
                <div className="qv-section-head">
                  <h2 className="qv-section-title">Choose content format</h2>
                  <p className="qv-section-subtitle">
                    Pick the structure that best fits your content.
                  </p>
                </div>

                {formats.length === 0 ? (
                  <div className="qv-empty">Loading formats…</div>
                ) : (
                  <div className="format-grid-xl">
                    {formats.map((format) => {
                      const isSelected = selectedFormat?.id === format.id;

                      return (
                        <div
                          key={format.id}
                          className={`format-card${isSelected ? " selected" : ""}${!format.is_active ? " disabled" : ""}`}
                          onClick={() => handleFormatSelect(format)}
                        >
                          <div className="format-badges">
                            {!format.is_active && <span className="badge coming-badge">Coming soon</span>}
                          </div>
                          <div className="format-card-header">
                            <span className="format-icon">
                              {React.createElement(getContentFormatIcon(format.slug), {
                                sx: { fontSize: 20, verticalAlign: "middle" },
                              })}
                            </span>
                            <span className="format-card-name">{format.name}</span>
                            {format.preview_config?.blocks ? (
                              <span className="format-blocks">
                                ~{String(format.preview_config.blocks)} blocks
                              </span>
                            ) : null}
                            {isSelected && <span className="format-check">✓</span>}
                          </div>
                          <div className="format-card-desc">
                            {format.short_description || format.description}
                          </div>
                          {format.best_for && format.best_for.length > 0 && (
                            <div className="format-best-for">
                              <span className="format-best-label">PERFECT FOR</span>
                              <div className="format-best-tags">
                                {format.best_for.map((tag) => (
                                  <span key={tag} className="format-tag">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {format.example_title && (
                            <div className="format-example">
                              <span className="format-example-label">Example</span>
                              <span className="format-example-title">
                                &ldquo;{format.example_title}&rdquo;
                              </span>
                            </div>
                          )}
                          <details className="format-card-preview" open={isMobile ? undefined : true}>
                            <summary
                              className="format-card-preview-toggle"
                              onClick={(event) => event.stopPropagation()}
                              onKeyDown={(event) => event.stopPropagation()}
                            >
                              Preview layout
                            </summary>
                            <div className="format-card-bottom">
                              <FormatPreview format={format} />
                            </div>
                          </details>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="qv-footer">
                <p className="qv-footer-note">
                  <SmartDisplayOutlinedIcon sx={{ fontSize: 16, color: "#66738a" }} />
                  Script generation and video rendering happen automatically
                </p>
                <button
                  type="button"
                  className="cv-btn cv-btn-primary qv-generate-btn"
                  disabled={!canGenerate}
                  onClick={handleCreateVideo}
                >
                  <BoltOutlinedIcon sx={{ fontSize: 18 }} />
                  Generate Video
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      {generating &&
        createPortal(
          <div className="qv-generate-overlay">
            <div className="qv-generate-overlay-content">
              <div className="qv-generate-overlay-spinner" />
              <p className="qv-generate-overlay-text">Building your video…</p>
              <div className="qv-generate-steps">
                {GENERATING_STEPS.map((label, i) => (
                  <div
                    key={label}
                    className={`qv-generate-step${i < generatingStep ? " done" : ""}${i === generatingStep ? " active" : ""}`}
                  >
                    <span className="qv-generate-step-dot" />
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default QuickVideo;
